// DraftingComponent.jsx — diffed background refetch + FULL-card burn + guaranteed avatar handoff
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { API_ENDPOINT } from "../../../../../../lib/api-url";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";

const ASSETS = {
  playerSlot: "/draft/player-slot.svg",
  playerCard: "/draft/player-card.svg",
  unavailablePlayerCard: "/draft/unavailable-player-card.svg",
  teamCard: "/draft/team-card.svg",
  alphawolvesMemberBg: "/draft/alphawolves-member-bg.svg",
  ironbearsMemberBg: "/draft/ironbears-member-bg.svg",
  goldeneaglesMemberBg: "/draft/goldeneagle-member-bg.svg",
  bullmastersMemberBg: "/draft/bullmaster-member-bg.svg",
  avatar: "/avatar/avatar-default.webp", // placeholder avatar (API provides none)
};

const TEAM_STAGGER = 0.32;
const TEAM_START = 0.1;
const TEAM_DURATION = 0.5;

const PLAYER_STAGGER = 0.12;
const PLAYER_DURATION = 0.35;
const OVERLAP = 0.2;

// Burn tuning
const BURN_DURATION = 1.6; // slower, more readable
const BURN_CARD_SCALE = 1.08; // full card puff
const FACE_SCALE_PEAK = 1.05; // face micro puff

// Smoother shared-element handoff
const HANDOFF_SPRING = {
  type: "spring",
  stiffness: 140,
  damping: 24,
  mass: 1.1,
};
// Slight settle for the final static fade-in
const SLOT_FADE = { duration: 0.45, ease: "easeOut" };

// ---------------- helpers
const getTeamKey = (t) => (t?.name || t?.title || "").toLowerCase().trim();
const getTeamMemberBg = (teamKey) => {
  switch (teamKey) {
    case "ironbears":
      return ASSETS.ironbearsMemberBg;
    case "bullmasters":
      return ASSETS.bullmastersMemberBg;
    case "goldeneagles":
      return ASSETS.goldeneaglesMemberBg;
    case "alphawolves":
      return ASSETS.alphawolvesMemberBg;
    default:
      return ASSETS.ironbearsMemberBg;
  }
};

function getNonCaptainMemberUserIds(team) {
  const captainId = Number(team?.captain_user_id ?? -1);
  const raw = team?.members ?? [];
  return (raw || [])
    .map((m) => {
      const role = (m?.role || "").toLowerCase();
      const uid =
        typeof m === "number" ? m : m?.user_id ?? m?.id ?? m?.user?.id ?? null;
      if (uid == null) return null;
      const nUid = Number(uid);
      if (role === "captain") return null;
      if (nUid === captainId) return null;
      return nUid;
    })
    .filter((x) => x != null);
}

export default function DraftingComponent() {
  const [teams, setTeams] = useState([]);
  const [finalistsRaw, setFinalistsRaw] = useState([]);
  const [loading, setLoading] = useState({ teams: false, finalists: false });
  const [error, setError] = useState({ teams: "", finalists: "" });

  // animation change queue: { [userId]: { toUnavailable: true, teamKey: string, at: number, handoffDone?: boolean } }
  const [changes, setChanges] = useState({});

  // last-seen maps kept in refs to diff against silently fetched data
  const unavailableIdsRef = useRef(new Set());
  const memberTeamRef = useRef(new Map()); // userId -> teamKey

  // ---------- base fetchers (initial load) ----------
  const fetchTeams = useCallback(async () => {
    setLoading((s) => ({ ...s, teams: true }));
    setError((e) => ({ ...e, teams: "" }));
    try {
      const url =
        API_ENDPOINT.wallTeams?.getIndex || API_ENDPOINT.adminTeams.getIndex;
      const res = await axios.get(url);
      const data = res?.data?.data ?? res?.data ?? [];
      setTeams(Array.isArray(data) ? data : []);
    } catch (err) {
      setError((e) => ({
        ...e,
        teams: err?.message || "خطا در دریافت تیم‌ها",
      }));
    } finally {
      setLoading((s) => ({ ...s, teams: false }));
    }
  }, []);

  const fetchFinalists = useCallback(async () => {
    setLoading((s) => ({ ...s, finalists: true }));
    setError((e) => ({ ...e, finalists: "" }));
    try {
      const res = await axios.get(API_ENDPOINT.finalists.getListOfFinalists);
      const list = res?.data?.items ?? res?.data?.data ?? [];
      setFinalistsRaw(Array.isArray(list) ? list : []);
    } catch (err) {
      setError((e) => ({
        ...e,
        finalists: err?.message || "خطا در دریافت فینالیست‌ها",
      }));
    } finally {
      setLoading((s) => ({ ...s, finalists: false }));
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchTeams(), fetchFinalists()]);
    })();
  }, [fetchTeams, fetchFinalists]);

  // ---------- derived data ----------
  const captainIds = useMemo(() => {
    const s = new Set();
    for (const t of teams || [])
      if (t?.captain_user_id != null) s.add(Number(t.captain_user_id));
    return s;
  }, [teams]);

  const finalists = useMemo(() => {
    const map = new Map();
    for (const f of finalistsRaw)
      if (!map.has(f.user_id)) map.set(f.user_id, f);
    return Array.from(map.values());
  }, [finalistsRaw]);

  const players = useMemo(() => {
    return finalists
      .filter((f) => !captainIds.has(Number(f.user_id)))
      .map((f) => ({
        id: Number(f.user_id),
        name:
          `${f.first_name || ""} ${f.last_name || ""}`.trim() ||
          `#${f.user_id}`,
        score: Number(f.points ?? f.score ?? 0),
        avatar: ASSETS.avatar, // placeholder avatar
      }));
  }, [finalists, captainIds]);

  const unavailableIds = useMemo(() => {
    const s = new Set();
    for (const t of teams || [])
      getNonCaptainMemberUserIds(t).forEach((uid) => s.add(Number(uid)));
    return s;
  }, [teams]);

  const userIdToTeamKey = useMemo(() => {
    const map = new Map();
    for (const t of teams || []) {
      const key = getTeamKey(t);
      for (const uid of getNonCaptainMemberUserIds(t))
        map.set(Number(uid), key);
    }
    return map;
  }, [teams]);

  // keep refs in sync after each render
  useEffect(() => {
    unavailableIdsRef.current = new Set(Array.from(unavailableIds));
    memberTeamRef.current = new Map(userIdToTeamKey);
  });

  // ---------- background refetch + diff ----------
  const silentRefetchAndDiff = useCallback(async () => {
    try {
      const [tRes, fRes] = await Promise.all([
        axios.get(
          API_ENDPOINT.wallTeams?.getIndex || API_ENDPOINT.adminTeams.getIndex
        ),
        axios.get(API_ENDPOINT.finalists.getListOfFinalists),
      ]);
      const newTeams = Array.isArray(tRes?.data?.data)
        ? tRes.data.data
        : tRes?.data || [];
      const newFinalists = Array.isArray(fRes?.data?.items)
        ? fRes.data.items
        : fRes?.data?.data || [];

      // compute new unavailable + team map
      const newUnavailable = new Set();
      const newUserToTeam = new Map();
      for (const t of newTeams) {
        const tKey = getTeamKey(t);
        for (const uid of getNonCaptainMemberUserIds(t)) {
          newUnavailable.add(Number(uid));
          newUserToTeam.set(Number(uid), tKey);
        }
      }

      // who just became unavailable?
      const prevUnavailable = unavailableIdsRef.current;
      const diffs = [];
      for (const uid of newUnavailable) {
        if (!prevUnavailable.has(uid)) {
          diffs.push({ uid, teamKey: newUserToTeam.get(uid) });
        }
      }

      // queue animations for newly added users
      if (diffs.length) {
        setChanges((prev) => {
          const copy = { ...prev };
          const now = Date.now();
          diffs.forEach(({ uid, teamKey }) => {
            copy[uid] = { toUnavailable: true, teamKey, at: now };
          });
          return copy;
        });
      }

      // update datasets
      setTeams(newTeams);
      setFinalistsRaw(Array.isArray(newFinalists) ? newFinalists : []);
    } catch (err) {
      console.error("silentRefetchAndDiff", err);
    }
  }, []);

  // event wiring
  useEffect(() => {
    const onDraftUpdated = () => {
      silentRefetchAndDiff();
    };

    const ce = () => onDraftUpdated();
    window.addEventListener("CAPTAINS_DRAFT_UPDATED", ce);

    let ch;
    try {
      ch = new BroadcastChannel("operator");
      ch.onmessage = (e) => {
        if (e?.data?.type === "CAPTAINS_DRAFT_UPDATED") onDraftUpdated();
      };
    } catch {}

    const onStorage = (e) => {
      if (e.key === "activeActions") {
        try {
          const actions = JSON.parse(e.newValue || "[]");
          const last = actions?.[actions.length - 1];
          if (last?.id === "CAPTAINS_DRAFT_UPDATED") onDraftUpdated();
        } catch {}
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("CAPTAINS_DRAFT_UPDATED", ce);
      window.removeEventListener("storage", onStorage);
      try {
        ch && ch.close();
      } catch {}
    };
  }, [silentRefetchAndDiff]);

  const teamViews = useMemo(() => {
    return (teams || []).slice(0, 4).map((t) => {
      const teamKey = getTeamKey(t);
      const memberIds = getNonCaptainMemberUserIds(t);
      return {
        id: t.id,
        teamKey,
        displayName: t?.name || t?.title || "",
        memberIds,
      };
    });
  }, [teams]);

  const anyLoading = loading.teams || loading.finalists;
  const anyError = error.teams || error.finalists;

  const slots = [1, 2, 3, 4, 5];

  const playersBaseDelay = useMemo(() => {
    const teamCount = teamViews?.length || 4;
    const totalTeams =
      TEAM_START + (teamCount - 1) * TEAM_STAGGER + TEAM_DURATION;
    return Math.max(0, totalTeams - OVERLAP);
  }, [teamViews]);

  const handlePlayerBurnComplete = useCallback((uid) => {
    setChanges((prev) => {
      const c = { ...prev };
      delete c[uid];
      return c;
    });
  }, []);

  // -------------- NEW: slot handoff completion (INSIDE COMPONENT!) --------------
  const handleSlotHandoffDone = useCallback((uid) => {
    setChanges((prev) => {
      const c = { ...prev };
      if (c[uid]) c[uid] = { ...c[uid], handoffDone: true };
      return c;
    });
  }, []);

  return (
    <LayoutGroup id="draft">
      <div className="flex flex-col w-full items-center justify-center gap-[62px] mb-[44px]">
        {/* Teams row */}
        <div className="listofteams-container w-full items-center justify-center flex gap-0 flex-row-reverse">
          {anyLoading ? (
            <div className="text-white/80 py-8">در حال بارگذاری…</div>
          ) : anyError ? (
            <div className="text-red-400 py-8">
              {(error.finalists && `خطای فینالیست‌ها: ${error.finalists}`) ||
                (error.teams && `خطای تیم‌ها: ${error.teams}`)}
            </div>
          ) : (
            (teamViews.length ? teamViews : Array.from({ length: 4 })).map(
              (tv, idx) => {
                const teamKey = tv?.teamKey ?? "ironbears";
                const displayName = tv?.displayName ?? "—";
                const memberIds = tv?.memberIds ?? [];

                return (
                  <motion.div
                    className="team-card-container relative"
                    key={tv?.id ?? idx}
                    initial={{ opacity: 0, y: 20, filter: "blur(2px)" }}
                    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{
                      duration: TEAM_DURATION,
                      ease: "easeOut",
                      delay: TEAM_START + idx * TEAM_STAGGER,
                    }}
                  >
                    <img className="w-[385px]" src={ASSETS.teamCard} alt="" />
                    <img
                      className="w-[80px] absolute right-[32px] top-[-24px]"
                      src={ASSETS.avatar}
                      alt=""
                    />
                    <div className="team-name absolute right-[142px] top-[32px]">
                      <span
                        style={{ fontFamily: "Etemad" }}
                        className="text-[#FFFFFF] text-[18px] font-normal "
                      >
                        {displayName}
                      </span>
                    </div>

                    <div className="team-slots w-full flex items-center justify-center gap-[9.5px] absolute left-[8px] bottom-[8px] flex-row-reverse ">
                      {[1, 2, 3, 4].map((i) => {
                        const uid = memberIds[i - 1];
                        const hasMember = Boolean(uid);
                        // if this member just got added, we’re in the middle of a handoff
                        const change = uid ? changes[uid] : undefined;
                        const isInHandoff = !!(
                          change?.toUnavailable && !change?.handoffDone
                        );

                        return (
                          <div key={i} className="relative">
                            {!hasMember ? (
                              <img
                                className="w-[55px]"
                                src={ASSETS.playerSlot}
                                alt=""
                              />
                            ) : (
                              <>
                                {/* Hide the background while the avatar is flying in so nothing appears early */}
                                <img
                                  className="w-[55px]"
                                  src={getTeamMemberBg(teamKey)}
                                  alt=""
                                  style={{ opacity: isInHandoff ? 0 : 1 }}
                                />

                                {/* While in handoff: mount the target with layoutId to receive the avatar */}
                                {isInHandoff ? (
                                  <motion.img
                                    layoutId={`avatar-${uid}`}
                                    src={ASSETS.avatar}
                                    className="w-[28px] absolute top-[2px] left-[12px] z-10"
                                    alt=""
                                    initial={false}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={HANDOFF_SPRING}
                                    onLayoutAnimationComplete={() =>
                                      handleSlotHandoffDone(uid)
                                    }
                                  />
                                ) : (
                                  // After handoff completes (or on initial load), show a static avatar with a subtle fade.
                                  <motion.img
                                    key={`slot-avatar-${uid}`} // ensure a clean non-layout node post-handoff
                                    src={ASSETS.avatar}
                                    className="w-[28px] absolute top-[2px] left-[12px] z-10"
                                    alt=""
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={SLOT_FADE}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              }
            )
          )}
        </div>

        {/* Players grid */}
        <div className="listofplayers-container flex flex-wrap w-[1650px] items-center justify-center gap-[42px] flex-row-reverse ">
          {anyLoading ? (
            <div className="text-white/80 py-8">در حال بارگذاری…</div>
          ) : anyError ? (
            <div className="text-red-400 py-8">
              {(error.finalists && `خطای فینالیست‌ها: ${error.finalists}`) ||
                (error.teams && `خطای تیم‌ها: ${error.teams}`)}
            </div>
          ) : players.length ? (
            players.map((p, idx) => {
              const isUnavailable = unavailableIds.has(p.id);
              const change = changes[p.id];
              return (
                <MemoPlayerCard
                  key={p.id}
                  p={p}
                  isUnavailable={isUnavailable}
                  change={change}
                  delay={playersBaseDelay + idx * PLAYER_STAGGER}
                  onBurnComplete={handlePlayerBurnComplete}
                />
              );
            })
          ) : (
            Array.from({ length: 16 }).map((_, i) => (
              <motion.div
                className="player-card-container relative w-[320px]"
                key={i}
                initial={{ opacity: 0, y: 10, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{
                  duration: PLAYER_DURATION,
                  ease: "easeOut",
                  delay: playersBaseDelay + i * PLAYER_STAGGER,
                }}
              >
                <img src={ASSETS.playerCard} alt="" />
                <div className="player-avatar absolute top-[4px] left-[0px] w-full flex items-center justify-center">
                  <span className="text-[#FFFFFF] font-bold text-[12px]">
                    امتیاز:—
                  </span>
                </div>
                <div className="player-name absolute top-[32px] right-[88px] flex items-center justify-center w-fit">
                  <span className="text-[#FFFFFF] font-bold text-[20px]">
                    —
                  </span>
                </div>
                <div className="avatar-container absolute top-[-18px] right-[22px] z-20">
                  <img src={ASSETS.avatar} className="w-[62px]" alt="" />
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </LayoutGroup>
  );
}

// ---------------- PlayerCard (memoized) ----------------
const PlayerCard = ({ p, isUnavailable, change, delay, onBurnComplete }) => {
  const [isBurning, setIsBurning] = useState(false);
  const [localHidden, setLocalHidden] = useState(false);

  // Kick off the burn when change arrives (do not hide yet)
  useEffect(() => {
    if (change?.toUnavailable && !isBurning) setIsBurning(true);
  }, [change?.toUnavailable, isBurning]);

  // Finish only AFTER slot confirms handoff; fallback by time
  useEffect(() => {
    if (!change?.toUnavailable) return;

    let fallback;
    if (change?.handoffDone) {
      setLocalHidden(true);
      setIsBurning(false);
      onBurnComplete?.(p.id);
    } else {
      fallback = setTimeout(() => {
        setLocalHidden(true);
        setIsBurning(false);
        onBurnComplete?.(p.id);
      }, BURN_DURATION * 1000 + 80);
    }
    return () => fallback && clearTimeout(fallback);
  }, [change?.toUnavailable, change?.handoffDone, onBurnComplete, p.id]);

  const showBackUnavailable = isUnavailable || localHidden || isBurning;
  const showAvatarInCard =
    (!isUnavailable && !localHidden) || (isBurning && !localHidden);

  return (
    <motion.div
      className="player-card-container relative w-[320px]"
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      animate={isBurning ? { scale: [1, BURN_CARD_SCALE, 1] } : { scale: 1 }}
      transition={{
        duration: isBurning ? BURN_DURATION : PLAYER_DURATION,
        ease: "easeOut",
        delay,
      }}
      style={isBurning ? { willChange: "transform" } : undefined}
      layout // participate in layout to help shared transition
    >
      {/* BACK LAYER */}
      <img
        src={ASSETS.unavailablePlayerCard}
        alt=""
        style={{ opacity: showBackUnavailable ? 1 : 0 }}
      />

      {/* FRONT LAYER with wipe mask */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute inset-0"
          style={{
            WebkitMaskImage: isBurning
              ? "linear-gradient(to right, black calc(var(--wipe,0%)), transparent calc(var(--wipe,0%)))"
              : undefined,
            maskImage: isBurning
              ? "linear-gradient(to right, black calc(var(--wipe,0%)), transparent calc(var(--wipe,0%)))"
              : undefined,
          }}
          animate={
            isBurning
              ? { "--wipe": [0, 100], scale: [1, FACE_SCALE_PEAK, 1] }
              : { "--wipe": 0, scale: 1 }
          }
          transition={
            isBurning
              ? { duration: BURN_DURATION, ease: "easeInOut" }
              : { duration: 0 }
          }
        >
          <img
            src={ASSETS.playerCard}
            alt=""
            style={{ opacity: localHidden || isUnavailable ? 0 : 1 }}
          />
        </motion.div>

        {/* fiery burn line */}
        <AnimatePresence>
          {isBurning && (
            <motion.div
              key="burn"
              className="absolute top-0 bottom-0 mix-blend-screen"
              initial={{ left: "0%", opacity: 0.0 }}
              animate={{
                left: "100%",
                opacity: [0.0, 1, 0.85, 1, 0.0],
                y: [0, -2, 0, 1, 0],
                width: [9, 13, 10, 12, 9],
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: BURN_DURATION, ease: "easeInOut" }}
              style={{
                filter: "blur(0.5px)",
                background:
                  "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(255,248,225,0.95) 35%, rgba(255,193,7,0.98) 55%, rgba(255,87,34,0.95) 75%, rgba(0,0,0,0) 100%)",
                boxShadow:
                  "0 0 24px 6px rgba(255,193,7,0.55), 0 0 36px 10px rgba(255,87,34,0.45)",
              }}
            />
          )}
        </AnimatePresence>
      </div>

      {/* content (score, name) */}
      <div className="player-avatar absolute top-[4px] left-[0px] w-full flex items-center justify-center">
        <span className="text-[#FFFFFF] font-bold text-[12px]">
          امتیاز:{p.score}
        </span>
      </div>
      <div className="player-name absolute top-[32px] right-[88px] flex items-center justify-center w-fit">
        <span className="text-[#FFFFFF] font-bold text-[20px]">{p.name}</span>
      </div>

      {/* Avatar on the card: this is the source for the shared handoff */}
      <div className="avatar-container absolute top-[-18px] right-[22px] z-20">
        <AnimatePresence initial={false}>
          {showAvatarInCard && (
            <motion.img
              key={`avatar-${p.id}`}
              layoutId={`avatar-${p.id}`}
              src={p.avatar}
              className="w-[62px]"
              alt=""
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0.9, scale: 0.98 }}
              transition={{
                type: "spring",
                stiffness: 220,
                damping: 26,
                mass: 1.2,
              }}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const MemoPlayerCard = React.memo(PlayerCard, (prev, next) => {
  const same =
    prev.p.id === next.p.id &&
    prev.p.name === next.p.name &&
    prev.p.score === next.p.score &&
    prev.isUnavailable === next.isUnavailable &&
    Boolean(prev.change?.toUnavailable) ===
      Boolean(next.change?.toUnavailable) &&
    Boolean(prev.change?.handoffDone) === Boolean(next.change?.handoffDone) &&
    prev.delay === next.delay;
  return same;
});
